/* eslint-disable no-magic-numbers */
/**
 * An "enum" of possible image sizes.
 */
// we use "as const" instead of an enum to keep this lib tree-shakeable
export const ImageSize = {
  ExtraExtraSmall: 'XXS',
  ExtraSmall: 'XS',
  Small: 'S',
  Medium: 'M',
  Large: 'L',
  ExtraLarge: 'XL',
  ExtraExtraLarge: 'XXL',
  ExtraExtraExtraLarge: 'XXXL',
} as const;

export type ImageSize = (typeof ImageSize)[keyof typeof ImageSize];

type TemplateMatch = {
  prefix: string;
  suffix: string;
  size: string;
  isPlaceholder: boolean;
};

/**
 * reference mapping from https://github.mpi-internal.com/bnl/media-handling-lib/blob/master/src/main/resources/media-handling-lib-defaults.properties
 * https://wiki.es.ecg.tools/display/MPTECH/Image+sizes
 * we have of course different mappings
 * https://github.mpi-internal.com/ecg-icas/icas/blob/master/src/go/src/cas/lib/eps/eps.go
 * https://github.mpi-internal.com/bnl/seller-profile-api/blob/master/src/main/scala/nl/marktplaats/sellerprofileapi/media/Media.scala
 * (https://github.mpi-internal.com/bnl/aurora-js-formatters/blob/master/src/formatImageUrl.ts)
 */
export const defaultImageSizeMap: Record<ImageSize, string> = {
  [ImageSize.ExtraExtraSmall]: '14',
  [ImageSize.ExtraSmall]: '14',
  [ImageSize.Small]: '14',
  [ImageSize.Medium]: '82',
  [ImageSize.Large]: '83',
  [ImageSize.ExtraLarge]: '84',
  [ImageSize.ExtraExtraLarge]: '85',
  [ImageSize.ExtraExtraExtraLarge]: '86',
};

/**
 * Image sizes are sometimes mapped to string keys. This is a default (and fallback) mapping used in e.g. {@link explodeToImageSizes}
 */
export const defaultImageSizeKeyMap: Record<ImageSize, string> = {
  [ImageSize.ExtraExtraSmall]: 'extraExtraSmall',
  [ImageSize.ExtraSmall]: 'extraSmall',
  [ImageSize.Small]: 'small',
  [ImageSize.Medium]: 'medium',
  [ImageSize.Large]: 'large',
  [ImageSize.ExtraLarge]: 'extraLarge',
  [ImageSize.ExtraExtraLarge]: 'extraExtraLarge',
  [ImageSize.ExtraExtraExtraLarge]: 'extraExtraExtraLarge',
};

const reverseMap: Record<string, ImageSize> = {
  extraextrasmall: ImageSize.ExtraExtraSmall,
  xxsmall: ImageSize.ExtraExtraSmall,
  xxs: ImageSize.ExtraExtraSmall,

  extrasmall: ImageSize.ExtraSmall,
  xsmall: ImageSize.ExtraSmall,
  xs: ImageSize.ExtraSmall,

  small: ImageSize.Small,
  s: ImageSize.Small,

  medium: ImageSize.Medium,
  m: ImageSize.Medium,
  md: ImageSize.Medium,

  large: ImageSize.Large,
  l: ImageSize.Large,

  extralarge: ImageSize.ExtraLarge,
  xlarge: ImageSize.ExtraLarge,
  xl: ImageSize.ExtraLarge,

  extraextralarge: ImageSize.ExtraExtraLarge,
  xxlarge: ImageSize.ExtraExtraLarge,
  xxl: ImageSize.ExtraExtraLarge,

  extraextraextralarge: ImageSize.ExtraExtraExtraLarge,
  xxxlarge: ImageSize.ExtraExtraExtraLarge,
  xxxl: ImageSize.ExtraExtraExtraLarge,
};

/*
 * some services anchor with `$_#`, some with _#. We settle on `_#` (and `_XX` with X is a number).
 * examples:
 * * https://github.mpi-internal.com/bnl/icas-buyside-service/blob/eeca5aeacf7ac395d10ab5fadcc69f40d096b7a6/src/main/scala/nl/marktplaats/icas/buyside/response/IcasMediaConverter.scala#L17
 * * https://github.mpi-internal.com/bnl/Marktplaats_app_iOS/blob/3f3777ddc18098f7754087a31b4e7b552d128334/MarktplaatsCore/Models/CurrentUser.swift#L322
 * * https://github.mpi-internal.com/bnl/marktplaats-app-android/blob/2df42939f295cd0deb92d7a6ef1f8624115dcca6/core/utils/src/main/java/com/horizon/android/core/utils/images/ImageUtils.java#L35
 */
const protocolRegex = /^[A-Za-z]+:\/\//;
const mpYamsUrlRegex = /(ecg_mp_eps\$_)(#|\d+)(\.jpg)?$/;

function maybeSplitMpYamsUrl(url: string) {
  // MP yams: that looks like an already sized image for yams
  const match = url.match(mpYamsUrlRegex);
  if (!match) {
    return null;
  }
  const prefix = url.substring(0, match.index) + match[1];
  const size = match[2];
  const suffix = match[3] || '';
  const isPlaceholder = size === '#';

  return { prefix, size, suffix, isPlaceholder };
}

function maybeSplitIcasPlaceholderUrl(url: string) {
  // iCAS, direct placeholder match
  // FYI: the original placeholder of iCAS is %s, but a backend service is replacing %s with #
  // so it aligns with the internal placeholders from marktplaats
  const idx = url.indexOf('#');
  return idx >= 0
    ? {
        prefix: url.substring(0, idx),
        size: '#',
        suffix: url.substring(idx + 1),
        isPlaceholder: true,
      }
    : null;
}

function maybeSplitEpsLegacyUrl(url: string) {
  // mp yams not matched, let's try the legacy stuff
  const match = url.match(/\$_(#|\d+)(\.[A-Za-z]+)$/);
  if (!match) {
    return null;
  }

  const size = match[1];
  const isPlaceholder = size === '#';
  const prefix = `${url.substring(0, match.index)}$_`;
  const suffix = match[2] || '';

  return { prefix, size, suffix, isPlaceholder };
}

// https://stackoverflow.com/a/59787784
export function isEmptyObject(obj: any): boolean {
  if (!obj) {
    // consider falsy, e.g. undefined values also as empty
    return true;
  }
  for (const _ in obj) {
    return false;
  }
  return true;
}

function isValidImageSize(s: unknown): s is ImageSize {
  return Object.values(ImageSize).includes(s as ImageSize);
}

function matchTemplateUrl(
  url: string,
  imageSizeMap: Record<ImageSize, string | number> = defaultImageSizeMap,
): TemplateMatch | null {
  // order is important
  const match = maybeSplitMpYamsUrl(url) ?? maybeSplitEpsLegacyUrl(url) ?? maybeSplitIcasPlaceholderUrl(url);

  if (!match) {
    return null;
  }

  const { size, suffix, prefix, isPlaceholder } = match;

  // either it is a placeholder or a _known_ size
  if (
    !(
      typeof size === 'string' &&
      (isPlaceholder ||
        Object.values(imageSizeMap)
          .map((v) => v.toString())
          .includes(size))
    )
  ) {
    return null;
  }

  return { prefix, size, suffix, isPlaceholder };
}

/**
 * Tries to parse an image size string.
 *
 * @param size a string that corresponds to a image size. {@see reverseMap} if you want to know the details.
 * @param fallback if we cannot parse, use this fallback size
 * @returns a image size from the {@link ImageSize} enum
 */
export function parseImageSize(size: string | unknown, fallback: ImageSize): ImageSize {
  if (!isValidImageSize(fallback)) {
    throw new Error('no valid fallback defined');
  }
  if (typeof size !== 'string' || !size) {
    return fallback;
  }
  return reverseMap[size.toLowerCase()] || fallback;
}

/**
 * Takes an URL and adds https:// in front of it, if needed
 *
 * @param url
 * @returns if the url is already with **http or https**, the original url, otherwise the
 * original url prefixed with https
 */
export function withHttps(url: string | unknown): string {
  if (typeof url !== 'string') {
    return '';
  }

  if (url === '') {
    return '';
  }

  if (url.toLowerCase().startsWith('http://') || url.toLowerCase().startsWith('https://')) {
    return url;
  }

  return url?.startsWith('//') ? `https:${url}` : `https://${url}`;
}

/**
 * Takes a URL, removes the existing protocol (if present) and adds https:// in front of it.
 * This means it also changes http to https.
 * Also handles URLs (starting with `//`)
 *
 * @param url
 * @returns if the url is already https, the original url, otherwise the original url prefixed with https.
 */
export function forceHttps(url: string | unknown): string {
  if (typeof url !== 'string') {
    return '';
  }

  if (url === '') {
    return '';
  }

  if (url.toLowerCase().startsWith('https://')) {
    return url;
  }

  return url.startsWith('//') ? `https:${url}` : `https://${url.replace(protocolRegex, '')}`;
}

/**
 * Prefixes {@link https://en.wikipedia.org/wiki/Uniform_Resource_Locator#prurl|protocol-relative URLs} (URLs starting with `//`) with `https:`.
 * @param url
 * @returns if the url starts with `//` return `https://url`, the original url otherwise
 */
export function withHttpsWhenPRURL(url: string | unknown): string {
  if (typeof url !== 'string') {
    return '';
  }

  return url.startsWith('//') ? `https:${url}` : url;
}

export type ExplodeToImageSizesOptions = {
  keyMap?: Partial<Record<ImageSize, string>> | undefined;
  sizeMap?: Record<ImageSize, number | string> | undefined;
};

/**
 * "Explodes" an image url to a dict/object of multiple sizes.
 * @example
 * explodeToImageSizes("//abc/def?rule=ecg_mp_eps$_#.jpg", [
 *   ImageSize.ExtraExtraLarge,
 *   ImageSize.ExtraLarge,
 * ]);
 * // returns:
 * // {
 * //    extraLarge: "https://abc/def?rule=ecg_mp_eps$_84.jpg",
 * //    extraExtraLarge: "https://abc/def?rule=ecg_mp_eps$_85.jpg",
 * // }
 * @param templateUrl a template url, e.g. "//abc/def?rule=ecg_mp_eps$_#.jpg"
 * or "//i.ebayimg.com/00/s$_#.JPG/1U/$_#.JPG".
 * @param sizes desired sizes
 * @param {ExplodeToImageSizesOptions} options
 * * you can supply a `keyMap` to influence the naming of the returned keys in the key => url mapping.
 *   If `keyMap` is not supplied {@link defaultImageSizeKeyMap} is used.
 * * you can supply a custom `sizeMap`. To be used with API responses that supply also a size
 *   to replacement map. An example is {@link defaultImageSizeMap}.
 * @returns a size to url map
 */
export function explodeToImageSizes(
  templateUrl: string | unknown,
  sizes: ReadonlyArray<ImageSize>,
  options?: ExplodeToImageSizesOptions,
): { [k: string]: string } {
  if (typeof templateUrl !== 'string' || !templateUrl || sizes.length === 0) {
    return {};
  }

  if (options && !isEmptyObject(options) && !(options.hasOwnProperty('keyMap') || options.hasOwnProperty('sizeMap'))) {
    throw new Error('if you use options, you have to supply either keyMap or sizeMap');
  }

  const match = matchTemplateUrl(templateUrl);
  if (!match) {
    return {};
  }

  const prefix = withHttpsWhenPRURL(match.prefix);
  const { suffix } = match;

  const { keyMap = defaultImageSizeKeyMap, sizeMap } = options || {};

  const safeSizeMap = sizeMap
    ? Object.fromEntries(Object.entries(sizeMap).map(([k, v]) => [k, v.toString()]))
    : defaultImageSizeMap;

  return Object.fromEntries(
    sizes
      .filter(isValidImageSize)
      .filter((s) => safeSizeMap.hasOwnProperty(s) && keyMap.hasOwnProperty(s))
      .map((s) => [keyMap[s], `${prefix}${safeSizeMap[s]}${suffix}`]),
  );
}

/**
 * to make use of the YAMS image auto-format feature, we need to remove the .jpg extension if present in the url.
 * @param url
 * @returns url without the .jpg or .JPG extension
 */
export function getURlWithoutJPG(url: string) {
  return url.replace(/\.jpg$/i, '');
}

export type ChangeToImageSizeOptions = {
  allowPlaceholder?: boolean;
  allowedSizes?: ImageSize[];
  customSizeMap?: Record<ImageSize | string, number | string>;
};

/**
 * Changes the image size of an image url.
 * Can also change template urls (urls with `_#` in it)
 * @param url
 * @param targetSize
 * @param {ChangeToImageSizeOptions} options You can modify/restrict the replacement behavior.
 * @deprecated use changeToImageSizeAndRemoveExtension instead.
 * If `allowPlaceholder` (default false) is set to true, images with `_#` are changed.
 *
 * The option `allowedSizes` is mainly for backwards compatibility: a lot of BFFs have something like:
 *
 * ```
 * url.replace('_82.JPG', '_83.JPG')
 * ```
 *
 * This means **only** image URLs with size 82 (Medium) are replaced.
 * Honestly I don't know if this
 * makes sense or not, but to make the migration possible and 100% backwards compatible, the
 * `allowedSizes` parameter was introduced.
 *
 * * To only replace URLs of certain (allowed) sizes, you have to supply a list of {@link ImageSize}s.
 * * To replace only placeholders, set `allowPlaceholder` to true  and `allowedSizes` to `[]`(empty list).
 * * If `allowedSizes` is omitted, all sizes are allowed.
 *
 * @returns the changed url or the original url if we could not match without .jpg/.JPG extension.
 */
export function changeToImageSize(
  url: string | unknown,
  targetSize: ImageSize,
  options?: ChangeToImageSizeOptions,
): string {
  if (typeof url !== 'string' || !url) {
    return '';
  }
  const urlWithoutExtension = getURlWithoutJPG(url);

  if (!isValidImageSize(targetSize)) {
    return urlWithoutExtension;
  }

  const { allowPlaceholder, allowedSizes, customSizeMap } = { allowPlaceholder: false, ...options };

  const imageSizeMap = customSizeMap || defaultImageSizeMap;

  const match = matchTemplateUrl(url, imageSizeMap);
  if (!match) {
    return urlWithoutExtension;
  }

  const replacementUrl = getURlWithoutJPG(`${match.prefix}${imageSizeMap[targetSize]}${match.suffix}`);

  if (match.isPlaceholder) {
    return allowPlaceholder ? replacementUrl : urlWithoutExtension;
  }

  if (!allowedSizes) {
    // we don't care about the existing size
    return replacementUrl;
  }

  const allowedMatches = allowedSizes
    ?.filter((s) => isValidImageSize(s) && imageSizeMap[s])
    ?.map((s) => imageSizeMap[s].toString());

  if (allowedMatches && allowedMatches.length > 0 && allowedMatches.includes(match.size)) {
    return replacementUrl;
  }

  return urlWithoutExtension;
}

// renaming changeToImageSize function. todo: cleanup when all bffs are using the new name
export const changeToImageSizeAndRemoveExtension = changeToImageSize;

/**
 * convenience function, replace the placeholder `_#` with the target image size. Same as
 * ```
 * changeToImageSize(url, targetSize, { allowPlaceholder: true, allowedSizes: []})
 * ```
 * Does NOT format image URLs that have already sizes, e.g. `_82`! See
 * {@link changeToImageSize} for more detailed info about formatting image URLs.
 * @param url
 * @param targetSize
 * @param customSizeMap
 * @returns a formatted image URL when placeholder matches, the original url otherwise.
 */
export function formatImageUrl(
  url: string | unknown,
  targetSize: ImageSize,
  customSizeMap?: ChangeToImageSizeOptions['customSizeMap'],
): string {
  return changeToImageSize(url, targetSize, {
    allowPlaceholder: true,
    allowedSizes: [],
    customSizeMap,
  });
}

/**
 * @deprecated **Do not resize images, always derive sized image URLs from the
 * original URL (the one with the placeholder)**
 *
 * convenience function to switch the image size of a url. Same as
 * ```
 * changeToImageSize(url, to, { allowedSizes: [from] })
 * ```
 *
 * **Does not support iCAS URLs.**
 * **Does NOT handle image URLs with placeholders (`_#`)!**
 *
 * See {@link changeToImageSize} for more detailed info about changing sizes.
 * @param url
 * @param from
 * @param to
 * @param customSizeMap
 * @returns a image URL with the new size, if `from` sizes matches, the original url otherwise.
 */
export function switchImageSize(
  url: string | unknown,
  from: ImageSize,
  to: ImageSize,
  customSizeMap?: ChangeToImageSizeOptions['customSizeMap'],
) {
  return changeToImageSize(url, to, { allowedSizes: [from], customSizeMap });
}
